/**
 * ContractorDocView (E4b Items 2 + 3) — the contractor WORK ORDER and the
 * contractor ESTIMATE.
 *
 * ONE component, two variants, because the spec defines them as the same
 * document with two differences. Two files would drift on the first change to
 * the pay table or the access tiles.
 *
 *   work order  — client contact section; full street addresses
 *   estimate    — NO client contact at all; city and province only, plus a line
 *                 saying contact details are released on the work order
 *
 * The withholding is enforced in the PAYLOAD (routes/portal.ts never puts a name,
 * phone or street into an estimate response), so this view cannot leak them by a
 * rendering mistake. What is done here is presentation only.
 *
 * Structure, top to bottom (ONE layout for long distance and local):
 *   header (job type; local: move date + arrival, LD: status) · client ·
 *   pick up / delivery side by side ·
 *   LOCAL work order: crew and time · client charges (estimate)
 *   LD: weights ·
 *   contractor pay and terms (LOCAL: screen only, never printed — the client
 *   may see the printed copy) · notes ·
 *   work order page 2: the inventory (cube sheet, or the written list).
 * Real tables throughout; links and buttons never print (.pp-noprint).
 */

import React from 'react';
import { PortalActions } from './PortalActions';
import { formatMoney } from '../../lib/utils';
import { chargeRule } from './chargeRule';
import { Icons } from '../../lib/icons';
import { downloadPortalPdf, DownloadState } from './downloadPdf';

/**
 * The sub-line under a pay charge — "$0.77 / lb × 2,000 lb".
 *
 * Composed HERE rather than server-side so money formatting stays in formatMoney,
 * and so the work order and the estimate can only ever render a rule one way.
 * Returns null when the line has no rule to state (a flat amount), or when the
 * document was snapshotted before E4b widened the snapshot — those degrade to a
 * bare amount rather than showing a recomputed figure that might not match the
 * locked total.
 */
export function payRule(l: any): string | null {
  // Delegates to the ONE rule builder (./chargeRule) so the contractor pay table
  // and the client invoice cannot state the same rule two different ways.
  return chargeRule(l);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const ymd = (v: any) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v ?? ''));
  return m ? { y: +m[1], m: +m[2] - 1, d: +m[3] } : null;
};
/** "Oct 8, 2026"; a range "Oct 8 – Oct 16, 2026" (the year once when both share it). */
export function dateWithYear(v: any): string {
  const a = ymd(v);
  return a ? `${MONTHS[a.m]} ${a.d}, ${a.y}` : '';
}
export function rangeWithYear(start: any, end: any): string {
  const a = ymd(start); const b = ymd(end);
  if (a && b) {
    if (a.y === b.y && a.m === b.m && a.d === b.d) return dateWithYear(start);
    return a.y === b.y ? `${MONTHS[a.m]} ${a.d} – ${MONTHS[b.m]} ${b.d}, ${b.y}` : `${dateWithYear(start)} – ${dateWithYear(end)}`;
  }
  return dateWithYear(start) || dateWithYear(end);
}

function windowText(w: any): string {
  if (!w) return 'To be confirmed';
  if (w.label) return w.label;
  return rangeWithYear(w.start, w.end) || 'To be confirmed';
}

/** "4 h 15 min" — hours stored as decimals, shown the way a crew says them. */
export function hm(hours: any): string {
  const n = Number(hours);
  if (hours == null || hours === '' || !Number.isFinite(n)) return '—';
  const total = Math.round(n * 60);
  const h = Math.floor(total / 60); const m = total % 60;
  return h && m ? `${h} h ${m} min` : h ? `${h} h` : `${m} min`;
}

const num = (v: any) => (v == null || v === '' ? null : Number(v));

/** The Rate column of an Item | Rate | Qty | Amount row (client or contractor). */
function rateCell(l: any): string {
  if (l.rateKind === 'pct') return l.percentage != null ? `${l.percentage}%` : l.rate != null ? `${l.rate}%` : '—';
  const r = num(l.rate);
  if (r == null || r === 0) return '—';
  const unit = l.unitLabel ? ` / ${l.unitLabel}` : l.rateKind === 'hourly' ? ' / hr' : l.rateKind === 'weight' ? ' / lb' : '';
  return `${formatMoney(r)}${unit}`;
}
function qtyCell(l: any): string {
  if (l.rateKind === 'pct') return '—';
  const q = num(l.quantity);
  if (q == null) return '—';
  const unit = l.unitLabel ?? (l.rateKind === 'weight' ? 'lb' : l.rateKind === 'hourly' ? 'hr' : '');
  return `${q.toLocaleString()}${unit ? ` ${unit}` : ''}`;
}

const crewText = (c: any) =>
  `${c.trucks} ${c.trucks === 1 ? 'truck' : 'trucks'}${c.movers ? ` · ${c.movers} movers` : ''}`;

function Leg({ title, leg, showStreet, local }: { title: string; leg: any; showStreet: boolean; local: boolean }) {
  // Local never uses a shuttle, so its tile is noise there: Flights, Walk, Elevator only.
  const access = (leg?.access ?? []).filter((t: any) => !(local && t.key === 'shuttle'));
  return (
    <div className="pp-leg">
      <div className="pp-sec">{title}</div>
      <div className="pp-when">{windowText(leg?.window)}</div>
      {showStreet && leg?.street ? (
        <>
          <div className="pp-addr">{leg.street}</div>
          <div className="pp-addr-sub">{leg.area}</div>
        </>
      ) : (
        <div className="pp-addr">{leg?.area || '—'}</div>
      )}
      <table className="pp-grid access" style={{ marginTop: 12 }}>
        <thead><tr>{access.map((t: any) => <th key={t.key}>{t.label}</th>)}</tr></thead>
        <tbody><tr>{access.map((t: any) => <td key={t.key} className={t.alert ? 'alert' : undefined}>{t.value}</td>)}</tr></tbody>
      </table>
    </div>
  );
}

function PayTable({ pay }: { pay: any }) {
  return (
    <table className="pp-grid">
      <thead><tr><th>Item</th><th className="num">Rate</th><th className="num">Qty</th><th className="num">Amount</th></tr></thead>
      <tbody>
        {(pay?.lines ?? []).map((l: any, i: number) => (
          <tr key={i}>
            <td>{l.desc}</td>
            <td className="num">{rateCell(l)}</td>
            <td className="num">{qtyCell(l)}</td>
            <td className="num">{formatMoney(l.amount)}</td>
          </tr>
        ))}
        <tr className="grand"><td colSpan={3}>Estimated total</td><td className="num">{formatMoney(pay?.total)}</td></tr>
      </tbody>
    </table>
  );
}

export function ContractorDocView({
  data, approvedAt, approving, onApprove, pdfPath,
}: {
  data: any;
  approvedAt: string | null;
  approving: boolean;
  onApprove: () => void;
  pdfPath: string;
}) {
  // P4 — PDF download state. Failure shows INLINE beside the button: a portal is
  // a client-facing page and an alert() is not an acceptable error surface.
  // The action row itself is hidden in the PDF by the stylesheet's @media print
  // rule, which Chromium honours via emulateMediaType('print') — one mechanism,
  // not a JSX conditional doing the same job a second way.
  const [pdf, setPdf] = React.useState<DownloadState>({ busy: false, error: null });
  const onDownload = async () => {
    setPdf({ busy: true, error: null });
    setPdf({ busy: false, error: await downloadPortalPdf(pdfPath) });
  };
  const doc = data.contractorDoc;
  const isWorkOrder = doc.kind === 'work_order';
  const local = doc.mode === 'local';
  const job = data.job ?? {};
  const inv = doc.inventory ?? {};
  const invd = doc.inventoryDetail ?? null;
  const lw = doc.local ?? null;
  const done = !!approvedAt;
  const statusText = done ? 'Approved' : doc.canApprove ? 'Awaiting approval' : doc.status;

  return (
    <div className="pp-wo">
      {/* 1. Header — Local: move date + arrival; Long distance: the status. */}
      <div className="pp-head">
        <div>
          <div className="pp-kicker">Baltic Van Lines</div>
          <h1 className="pp-h1">
            {isWorkOrder ? 'Work Order' : 'Job Estimate'} #{job.number}
            <span className="pp-wo-type">{local ? 'Local' : 'Long Distance'}</span>
          </h1>
        </div>
        {local && doc.moveDate ? (
          <div className="pp-wo-when">
            Move date<b>{dateWithYear(doc.moveDate)}</b>
            {doc.arrival && <>Arrival <b>{doc.arrival}</b></>}
          </div>
        ) : (
          <span className={`pp-badge${done ? ' ok' : doc.canApprove ? ' warn' : ''}`}>{statusText}</span>
        )}
      </div>

      {/* 2. Client — WORK ORDER ONLY. The estimate payload has no name, phone
             or email in it at all, so there is nothing here to withhold. */}
      {isWorkOrder && (
        <div className="pp-card">
          <div className="pp-sec">Client</div>
          <table className="pp-grid">
            <thead><tr><th>Name</th><th>Phone</th><th>Email</th></tr></thead>
            <tbody><tr>
              <td>{job.customerName || '—'}</td>
              <td>{job.customerPhone || '—'}</td>
              <td style={{ overflowWrap: 'anywhere' }}>{job.customerEmail || '—'}</td>
            </tr></tbody>
          </table>
        </div>
      )}

      {/* 3. Pick Up / Delivery, side by side (also in print). */}
      <div className="pp-legs" style={{ marginBottom: 14 }}>
        <Leg title="Pick Up" leg={doc.pickup} showStreet={isWorkOrder} local={local} />
        <Leg title="Delivery" leg={doc.delivery} showStreet={isWorkOrder} local={local} />
      </div>

      {/* 4L. LOCAL work order: crew and time, then the client's estimate. */}
      {lw && (
        <>
          <div className="pp-card">
            <div className="pp-sec">Crew And Time</div>
            {/* Print: one row of five, label above value (keeps page 1 to one page). */}
            <table className="pp-grid row pp-printonly">
              <thead><tr><th>Crew Size</th><th>Hourly Rate</th><th>Minimum Hours</th><th>Travel (flat, dock to dock)</th><th>Estimated Hours (incl. travel)</th></tr></thead>
              <tbody><tr>
                <td>{crewText(lw.crew)}</td>
                <td>{formatMoney(lw.hourlyRate)} / hr</td>
                <td>{lw.minimumHours != null ? hm(lw.minimumHours) : '—'}</td>
                <td>{hm(lw.travelHours)}</td>
                <td>{hm(lw.estimatedHours)}</td>
              </tr></tbody>
            </table>
            <table className="pp-grid kv pp-noprint">
              <tbody>
                <tr><th>Crew Size</th><td>{crewText(lw.crew)}</td></tr>
                <tr><th>Hourly Rate</th><td>{formatMoney(lw.hourlyRate)} / hr</td></tr>
                <tr><th>Minimum Hours</th><td>{lw.minimumHours != null ? hm(lw.minimumHours) : '—'}</td></tr>
                <tr><th>Travel Time (flat, dock to dock)</th><td>{hm(lw.travelHours)}</td></tr>
                <tr><th>Estimated Hours (including travel)</th><td>{hm(lw.estimatedHours)}</td></tr>
              </tbody>
            </table>
          </div>
          <div className="pp-card pp-breakable">
            <div className="pp-sec">Client Charges (Estimate)</div>
            <table className="pp-grid">
              <thead><tr><th>Item</th><th className="num">Rate</th><th className="num">Qty</th><th className="num">Amount</th></tr></thead>
              <tbody>
                {lw.charges.lines.map((l: any, i: number) => (
                  <tr key={i}>
                    <td>{l.desc}</td>
                    <td className="num">{rateCell(l)}</td>
                    <td className="num">{qtyCell(l)}</td>
                    <td className="num">{formatMoney(l.amount)}</td>
                  </tr>
                ))}
                {lw.charges.discount > 0 && (
                  <tr className="sub"><td colSpan={3}>Discount</td><td className="num">−{formatMoney(lw.charges.discount)}</td></tr>
                )}
                <tr className="sub"><td colSpan={3}>{lw.charges.taxLabel}</td><td className="num">{formatMoney(lw.charges.tax)}</td></tr>
                <tr className="sub"><td colSpan={3}>Deposit paid</td><td className="num">−{formatMoney(lw.charges.depositPaid)}</td></tr>
                <tr className="grand"><td colSpan={3}>Estimated to collect</td><td className="num">{formatMoney(lw.charges.toCollect)}</td></tr>
              </tbody>
            </table>
            <p className="pp-sub" style={{ marginTop: 10 }}>
              Billed hours = travel + on-site time, rounded up to the next 15 min, at least the minimum. Call the office with your times for the final invoice. Tips are extra and not taxed.
            </p>
          </div>
        </>
      )}

      {/* 4D. LONG DISTANCE: weights. */}
      {!local && (
        <div className="pp-card">
          <div className="pp-sec">Weights</div>
          {/* No "expected range": nothing stores one, and inventing one on a
              document a contractor prices from is worse than showing nothing. */}
          <table className="pp-grid">
            <thead><tr><th>Minimum Weight</th><th>Estimated Weight</th></tr></thead>
            <tbody><tr>
              <td>{Number(doc.weights?.minimumWeight ?? 0).toLocaleString()} lb</td>
              <td>{Number(doc.weights?.estimatedWeight ?? 0).toLocaleString()} lb</td>
            </tr></tbody>
          </table>
        </div>
      )}

      {/* 5. Contractor pay. LOCAL: on screen only — the printed local work
             order may be handed to the client, so it never carries the pay. */}
      <div className={`pp-card${local ? ' pp-noprint' : ''}`}>
        <div className="pp-sec">Contractor Pay</div>
        <div className="pp-wrap"><PayTable pay={doc.pay} /></div>
        <p className="pp-sub" style={{ marginTop: 10 }}>
          {local ? 'Shown on screen only — not printed on the work order.' : 'Final pay is calculated on the actual scale weight.'}
        </p>
      </div>

      {/* 6. Inventory summary + link — the ESTIMATE (the work order prints it in full on page 2). */}
      {!isWorkOrder && (
        <div className="pp-card">
          <div className="pp-inv-row">
            <div>
              <div className="pp-sec" style={{ marginBottom: 4 }}>Inventory</div>
              <div className="pp-sub">
                {inv.status === 'ready'
                  ? `${Number(inv.itemCount || 0).toLocaleString()} items${inv.cuft ? ` · ${Number(inv.cuft).toLocaleString()} cu ft` : ''}`
                  : 'No inventory recorded yet.'}
              </div>
            </div>
            {doc.inventoryUrl && (
              <a className="pp-btn pp-noprint" href={doc.inventoryUrl} target="_blank" rel="noopener noreferrer" style={{ flex: '0 0 auto' }}>
                <Icons.eye size={16} aria-hidden="true" /> View inventory
              </a>
            )}
          </div>
        </div>
      )}

      {/* 7. Terms — numbered (the wording is Settings content). LOCAL: on screen
             only, like the contractor pay — the printed copy may go to the client. */}
      {(doc.terms ?? []).length > 0 && (
        <div className={`pp-card${local ? ' pp-noprint' : ''}`}>
          <div className="pp-sec">Terms</div>
          <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: 'var(--soft)', display: 'grid', gap: 6 }}>
            {(doc.terms ?? []).map((t: string, i: number) => <li key={i}>{t}</li>)}
          </ol>
          {!isWorkOrder && (
            <p className="pp-sub" style={{ marginTop: 12 }}>
              Full client contact details and exact addresses are released on the work order once the job is approved.
            </p>
          )}
        </div>
      )}

      {doc.notes && (
        <div className="pp-card">
          <div className="pp-sec">Notes</div>
          <div className="pp-sub" style={{ whiteSpace: 'pre-wrap' }}>{doc.notes}</div>
        </div>
      )}

      {/* 8. Inventory — page 2 of the work order (both job types). Not a card:
             a long list must be free to run onto further pages. */}
      {isWorkOrder && invd && (
        <div className="pp-pagebreak" style={{ marginTop: 14 }}>
          <div className="pp-sec">Inventory (provided by the client)</div>
          {invd.rows.length > 0 ? (
            <table className="pp-grid">
              <thead><tr><th>Room</th><th>Item</th><th className="num">Qty</th><th className="num">Cu ft</th></tr></thead>
              <tbody>
                {invd.rows.map((r: any, i: number) => (
                  <tr key={i}><td>{r.room}</td><td>{r.item}</td><td className="num">{Number(r.qty).toLocaleString()}</td><td className="num">{Number(r.cuft || 0).toLocaleString()}</td></tr>
                ))}
                <tr className="grand"><td colSpan={2}>Total</td><td className="num">{Number(invd.totals.qty).toLocaleString()}</td><td className="num">{Number(invd.totals.cuft || 0).toLocaleString()}</td></tr>
              </tbody>
            </table>
          ) : invd.writtenList ? (
            <div className="pp-sub" style={{ whiteSpace: 'pre-wrap', color: 'var(--ink)', fontSize: 14 }}>{invd.writtenList}</div>
          ) : (
            <div className="pp-sub">No inventory recorded yet.</div>
          )}
          {doc.inventoryUrl && (
            <a className="pp-btn pp-noprint" href={doc.inventoryUrl} target="_blank" rel="noopener noreferrer" style={{ marginTop: 12, flex: '0 0 auto' }}>
              <Icons.eye size={16} aria-hidden="true" /> View inventory
            </a>
          )}
        </div>
      )}

      {/* 9. Actions — screen only. */}
      {doc.canApprove && !done && <p className="pp-sub pp-noprint" style={{ margin: '14px 0 8px' }}>{doc.approvalNote}</p>}
      <PortalActions>
        {/* P4: the server prints THIS page, so the file matches what you see.
            Work order only — the contractor estimate has no PDF. */}
        {isWorkOrder && (
          <button className="pp-btn" onClick={onDownload} disabled={pdf.busy}>
            <Icons.document size={16} aria-hidden="true" /> {pdf.busy ? 'Preparing…' : 'Download PDF'}
          </button>
        )}
        {done ? (
          <div className="pp-done"><Icons.check size={16} aria-hidden="true" /> Approved</div>
        ) : doc.canApprove ? (
          <button className="pp-btn primary" onClick={onApprove} disabled={approving}>
            {approving ? 'Recording…' : 'Approve'}
          </button>
        ) : null}
      </PortalActions>
      {pdf.error && <div className="pp-pdf-error pp-noprint">{pdf.error}</div>}
    </div>
  );
}
